import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupIcon from "@mui/icons-material/Group";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableChartIcon from "@mui/icons-material/TableChart";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import * as XLSX from "xlsx";
import BackBtn from "../widgets/BackBtn";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

// --- UTILITY FUNCTIONS FOR EMAIL GENERATION ---

const DEFAULT_DP = "https://via.placeholder.com/50";
const TimeZoneIndia = { timeZone: "Asia/Kolkata" };

export function getCatalogueTemplate(user, supplier, headerUrl, footerUrl) {
  const groupedCategories = (user.productCategories || []).reduce(
    (acc, { mainCat, subCat, subSubCat }) => {
      if (!acc[mainCat]) acc[mainCat] = {};
      if (!acc[mainCat][subCat]) acc[mainCat][subCat] = [];
      acc[mainCat][subCat].push(subSubCat);
      return acc;
    },
    {}
  );

  const formattedCategories = Object.entries(groupedCategories)
    .map(([mainCat, subCats]) => {
      const subCatHTML = Object.entries(subCats)
        .map(
          ([subCat, subSubCats]) =>
            `<em>${subCat}:</em> ${subSubCats.join(", ")}`
        )
        .join("; ");
      return `<strong>${mainCat}:</strong> ${subCatHTML}`;
    })
    .join("<br>");

  const sanitize = (input) => (input ? String(input).replace(/[<>]/g, "") : "");

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Catalogue</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f5f5f5; margin: 0; padding: 0; line-height: 1.6; color: #333; }
          .container { max-width: 800px; margin: 20px auto; padding: 24px; background-color: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
          .section { border: 1px solid #e0e0e0; padding: 16px; margin: 16px 0; border-radius: 6px; }
          .profile-image { width: 100px; max-width: 100%; display: block; margin: 16px auto; border-radius: 50%; }
          table { width: 100%; border-collapse: collapse; margin: 24px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e0e0e0; }
          th { background-color: #f8f8f8; font-weight: 600; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          img.header-footer { width: 100%; max-width: 800px; height: auto; display: block; margin: 0 auto; border-radius: 6px; }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${sanitize(
            headerUrl
          )}" alt="Header Image" class="header-footer" />
          <div class="section">
            <p><strong>From:</strong> Company: ${sanitize(
              user.companyName
            )}, Name: ${sanitize(user.contactSalutation)} ${sanitize(
    user.contactFirstName
  )} ${sanitize(user.contactLastName)}, Phone: ${sanitize(
    user.mobile
  )}, Email: ${sanitize(user.contactEmail)}</p>
          </div>
          <div class="section">
            <p><strong>To:</strong> Company: ${sanitize(
              supplier.supplierName
            )},<br />Address: ${sanitize(supplier.address)}</p>
            <p>Phone: ${sanitize(supplier.phoneNumber)}</p>
            <p>Email: ${sanitize((supplier.emails || []).join(", "))}</p>
          </div>
          ${
            user.profile_image
              ? `<img src="${sanitize(
                  user.profile_image
                )}" alt="Profile Image" class="profile-image" />`
              : ""
          }
          <table>
            <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td><strong>Company Name:</strong></td><td>${sanitize(
                user.companyName
              )}</td></tr>
              <tr><td><strong>Product Categories:</strong></td><td>${formattedCategories}</td></tr>
            </tbody>
          </table>
          <img src="${sanitize(
            footerUrl
          )}" alt="Footer Image" class="header-footer" />
        </div>
      </body>
    </html>
  `.trim();
}

export function getExhibitorInfoOes(user, titleString) {
  function containsSetupAndDismantling(name) {
    const regex = /setup/i;
    const regexDismantling = /dismantling/i;
    return regex.test(name) && regexDismantling.test(name);
  }

  const shouldShowDates = containsSetupAndDismantling(titleString);

  return `<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
    <tr><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Attribute</th><th style="padding: 10px; border-bottom: 1px solid #ddd; background-color: #f2f2f2; text-align:left;">Value</th></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Company Name:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.companyName?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Raw Space:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.spaceType?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Size:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standSize?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Hall Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.hallNumber?.value || "N/A"
    }</td></tr>
    <tr><td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Stand Number:</strong></td><td style="padding: 10px; border-bottom: 1px solid #ddd;">${
      user.standNumber?.value || "N/A"
    }</td></tr>
    ${
      shouldShowDates
        ? `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Exhibits Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.exhibitArrival?.value
              ? new Date(user.exhibitArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Material Arrival Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.materialArrival?.value
              ? new Date(user.materialArrival.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;"><strong>Dismantling Date:</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #ddd;">${
            user.dismantling?.value
              ? new Date(user.dismantling.value).toLocaleDateString(
                  "en-IN",
                  TimeZoneIndia
                )
              : "N/A"
          }</td>
        </tr>
    `
        : ""
    }
  </table>`;
}

export function oesTemplate(
  user,
  templateBody = "",
  supplier,
  formTitle = "",
  headerUrl,
  footerUrl
) {
  return `<!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Email Template</title>
      <style>
          body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 0; width: 100%; -webkit-text-size-adjust: none; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; background-color: #ffffff; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; }
      </style>
  </head>
  <body>
      <div class="container">
          ${
            headerUrl
              ? `<img src="${headerUrl}" alt="Header Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>From:</strong> Company: ${
                user.companyName?.value || "N/A"
              }, Name: ${user.contactSalutation?.value || ""} ${
    user.contactFirstName?.value || ""
  } ${user.contactLastName?.value || ""}, Phone: ${
    user.mobile?.value || "N/A"
  }, Email: ${user.contactEmail?.value || "N/A"} </p>
          </div>
          <div style="border: 1px solid #000; padding: 10px; margin: 10px;">
              <p> <strong>To:</strong> Company: ${
                supplier.supplierName || "N/A"
              },<br /> Address: ${supplier.address || "N/A"} </p>
              <p>Phone number: ${supplier.phoneNumber || "N/A"}</p>
              <p>Email: ${supplier.emails?.join(",") || "N/A"}</p>
          </div> 
          ${getExhibitorInfoOes(user, formTitle)}<br />
          ${templateBody}<br />
          ${
            footerUrl
              ? `<img src="${footerUrl}" alt="Footer Image" style="display: block; margin: 0 auto; width: 100%;" />`
              : ""
          }
      </div>
  </body>
  </html>`;
}

function subTotal(rowIndex, headerIndex, myItems, headers) {
  const formulaString = headers[headerIndex]["formula"];
  const suffixes =
    formulaString
      .match(/key(\d+)/g)
      ?.map((match) => match.replace("key", "")) || [];

  const values = [];
  for (const suffix of suffixes) {
    const ci = parseInt(suffix) - 1;
    const type = headers[ci]?.type;
    let value = 0;
    if (type === "input number") {
      value = parseInt(myItems[rowIndex][`value${ci}`]) || 0;
    } else if (type === "label") {
      value = parseInt(myItems[rowIndex][`key${suffix}`]) || 0;
    } else if (type === "input checkbox") {
      value = myItems[rowIndex][`value${ci}`] ? 1 : 0;
    } else if (type === "label checkbox") {
      if (myItems[rowIndex][`value${ci}`]) {
        value =
          parseInt(myItems[rowIndex][`key${suffix}`]?.replace(/,/g, "")) || 0;
      } else {
        value = 0;
      }
    } else if (type === "input date range") {
      if (
        myItems[rowIndex][`dateTo${ci}`] &&
        myItems[rowIndex][`dateFrom${ci}`]
      ) {
        const dateFrom = new Date(myItems[rowIndex][`dateFrom${ci}`]);
        const dateTo = new Date(myItems[rowIndex][`dateTo${ci}`]);
        const diffInDays =
          Math.abs((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
        value = diffInDays;
      }
    }
    values.push(value);
  }

  let updatedFormula = formulaString;
  suffixes.forEach((suffix, index) => {
    const keyPattern = new RegExp(`key${suffix}`, "g");
    updatedFormula = updatedFormula.replace(keyPattern, values[index]);
  });

  let total = 0;
  try {
    // eslint-disable-next-line no-eval
    total = eval(updatedFormula);
  } catch (error) {
    console.error("Error evaluating formula:", error);
  }
  return total;
}

function renderCell(header, row, i) {
  let cellHtml = "";
  if (header.type === "label") {
    const value = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "label checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    const label = row[`key${i + 1}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled> ${label}</td>`;
  } else if (header.type === "input text" || header.type === "input number") {
    const value = row[`value${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else if (header.type === "input date range") {
    const dateFrom = row[`dateFrom${i}`]
      ? new Date(row[`dateFrom${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    const dateTo = row[`dateTo${i}`]
      ? new Date(row[`dateTo${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">From: ${dateTo}<br>To: ${dateFrom}</td>`;
  } else if (header.type === "input checkbox") {
    const isChecked = row[`value${i}`] ? "checked" : "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"><input type="checkbox" ${isChecked} disabled></td>`;
  } else if (header.type === "calc") {
    const value = row[`calcVal${i}`] || "";
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;">${value}</td>`;
  } else {
    cellHtml += `<td style="padding: 10px; border: 1px solid #ddd;"></td>`;
  }
  return cellHtml;
}

export function generateRatedItemsHTML(
  headers,
  itemsList,
  isColumnSelection = false,
  selectedColumn = null,
  totalRequired = false
) {
  const columnsWithCheckedItems = new Set();
  if (isColumnSelection && selectedColumn != null) {
    itemsList.forEach((item) => {
      headers.forEach((header, i) => {
        if (
          i !== selectedColumn &&
          (header.type === "input checkbox" ||
            header.type === "label checkbox") &&
          item[`value${i}`]
        ) {
          columnsWithCheckedItems.add(i);
        }
      });
    });
  }

  const itemsWithSubtotal = itemsList.map((item, rowIndex) => {
    const newItem = { ...item };
    if (isColumnSelection && selectedColumn != null) {
      const isChecked = item[`value${selectedColumn}`];
      const valueKey = `key${selectedColumn + 1}`;
      const rawValue = item[valueKey];
      const value = parseFloat(String(rawValue).replace(/,/g, "")) || 0;
      newItem.subtotal = isChecked ? value : 0;
    } else {
      const calcIndex = headers.findIndex((item) => item.type === "calc");
      if (calcIndex !== -1) {
        newItem[`calcVal${calcIndex}`] = subTotal(
          rowIndex,
          calcIndex,
          itemsList,
          headers
        );
        newItem.subtotal = newItem[`calcVal${calcIndex}`] || 0;
      } else {
        newItem.subtotal = 0;
      }
    }
    return newItem;
  });

  const filteredItems = itemsWithSubtotal.filter((item) => {
    if (isColumnSelection && selectedColumn != null) {
      return item[`value${selectedColumn}`];
    } else {
      return item.subtotal > 0;
    }
  });

  const totalValue = totalRequired
    ? filteredItems.reduce((acc, item) => acc + (item.subtotal || 0), 0)
    : 0;

  let html =
    '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; margin-top: 15px;">';
  html += "<thead><tr>";
  headers.forEach((header, i) => {
    if (
      !isColumnSelection ||
      i === selectedColumn ||
      columnsWithCheckedItems.has(i) ||
      (header.type !== "input checkbox" && header.type !== "label checkbox")
    ) {
      html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">${header.header}</th>`;
    }
  });
  if (isColumnSelection && selectedColumn != null) {
    html += `<th style="padding: 10px; border: 1px solid #ddd; background-color: #f2f2f2;">Subtotal</th>`;
  }
  html += "</tr></thead><tbody>";

  filteredItems.forEach((row, rowIndex) => {
    html += "<tr>";
    headers.forEach((header, i) => {
      if (
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
      ) {
        html += renderCell(header, row, i);
      }
    });
    if (isColumnSelection && selectedColumn != null) {
      html += `<td style="padding: 10px; border: 1px solid #ddd;">${row.subtotal?.toLocaleString()}</td>`;
    }
    html += "</tr>";
  });
  html += "</tbody>";

  if (totalRequired) {
    const colspan = headers.filter(
      (header, i) =>
        !isColumnSelection ||
        i === selectedColumn ||
        columnsWithCheckedItems.has(i) ||
        (header.type !== "input checkbox" && header.type !== "label checkbox")
    ).length;
    const adjustedColspan =
      isColumnSelection && selectedColumn != null ? colspan : colspan - 1;

    html += "<tfoot>";
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">Total</td><td style="padding: 10px; border: 1px solid #ddd;">${totalValue?.toLocaleString()}</td></tr>`;
    const gst = Math.round(totalValue * 0.18);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;">18% GST</td><td style="padding: 10px; border: 1px solid #ddd;">${gst?.toLocaleString()}</td></tr>`;
    const grandTotal = Math.round(totalValue + gst);
    html += `<tr><td colspan="${adjustedColspan}" style="text-align: right; padding: 10px; border: 1px solid #ddd;"><strong>Grand Total</strong></td><td style="padding: 10px; border: 1px solid #ddd;"><strong>${grandTotal?.toLocaleString()}</strong></td></tr>`;
    html += "</tfoot>";
  }

  html += "</table>";
  return html;
}

export function generateMembersTableHTML(members) {
  let html = `
    <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; margin-top: 15px;">
      <thead>
        <tr style="background-color: #f2f2f2; font-weight: bold;">
          <th style="padding: 10px; border: 1px solid #ddd;">Profile Image</th>
          <th style="padding: 10px; border: 1px solid #ddd;">First Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Last Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Phone</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Email</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Role</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Designation</th>
        </tr>
      </thead>
      <tbody>
  `;
  (members || []).forEach((member) => {
    const profileImage = member.profile_image || DEFAULT_DP;
    html += `
      <tr>
        <td style="text-align: center; padding: 10px; border: 1px solid #ddd;">
          <img src="${profileImage}" alt="Profile Image" style="width: 50px; height: 50px; border-radius: 50%; display: block; margin: 0 auto;" />
        </td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.firstName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.lastName || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.phone || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.email || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.role || ""
        }</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${
          member.designation || ""
        }</td>
      </tr>
    `;
  });
  html += `</tbody></table>`;
  return html;
}

export function generateBadgeEntitlementHTML(properties, members, user) {
  const {
    basenum = 0,
    basesqm = 0,
    extranum = 0,
    extrasqm = 1,
  } = properties || {};
  const intBasenum = parseInt(basenum, 10) || 0;
  const intBasesqm = parseInt(basesqm, 10) || 0;
  const intExtranum = parseInt(extranum, 10) || 0;
  const intExtrasqm = parseInt(extrasqm, 10) || 1;

  const mySize = parseFloat(user?.standSize?.value || 0);
  const totalFreeBadges =
    intBasenum +
    Math.ceil(Math.max(0, mySize - intBasesqm) * (intExtranum / intExtrasqm));
  const totalMembers = (members || []).length;
  const paidBadges = Math.max(0, totalMembers - totalFreeBadges);

  return `
    <div style="max-width: 500px; margin: 15px auto; font-family: Arial, sans-serif;">
      <div style="border: 1px solid #ddd; padding: 16px; border-radius: 8px; background-color: #f5f5f5;">
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
          <tbody>
            <tr>
              <td style="padding: 8px; color: #555;">Total Free Badges:</td>
              <td style="padding: 8px; text-align: right; color: #007bff; font-weight: bold;">${totalFreeBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Paid Badges Required:</td>
              <td style="padding: 8px; text-align: right; color: #ff4c4c; font-weight: bold;">${paidBadges}</td>
            </tr>
            <tr>
              <td style="padding: 8px; color: #555;">Total Members:</td>
              <td style="padding: 8px; text-align: right; color: #333; font-weight: bold;">${totalMembers}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

export function generateCartTemplate(cartItems) {
  let subtotal = 0;
  let itemRows = "";

  (cartItems || []).forEach((item) => {
    const itemTotal = (item.price || 0) * (item.quantity || 0);
    subtotal += itemTotal;
    itemRows += `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${
          item.name || ""
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${
          item.quantity || 0
        }</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${(
          item.price || 0
        ).toFixed(2)}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${itemTotal.toFixed(
          2
        )}</td>
      </tr>
    `;
  });

  const gst = subtotal * 0.18;
  const grandTotal = subtotal + gst;

  return `
      <h2 style="margin-top: 20px;">Order Summary</h2>
      <table style="border-collapse: collapse; width: 100%; max-width: 600px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Item Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Quantity</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Price (₹)</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
        </tbody>
      </table>
      <p style="margin-top: 20px; font-size: 16px;"><strong>Subtotal:</strong> ₹${subtotal.toFixed(
        2
      )}</p>
      <p style="font-size: 16px;"><strong>GST (18%):</strong> ₹${gst.toFixed(
        2
      )}</p>
      <p style="font-size: 18px;"><strong>Grand Total:</strong> ₹${grandTotal.toFixed(
        2
      )}</p>
  `;
}


const asValueField = (value, fallback = "") => {
  if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value")) {
    return value;
  }
  return { value: value ?? fallback };
};

const normalizeExhibitorForEmail = (exhibitor = {}, submission = {}) => ({
  ...exhibitor,
  companyName: asValueField(
    exhibitor.companyName,
    submission.exhibitorName || submission.companyName || "N/A"
  ),
  spaceType: asValueField(exhibitor.spaceType, "N/A"),
  standSize: asValueField(exhibitor.standSize, "N/A"),
  hallNumber: asValueField(
    exhibitor.hallNumber ?? exhibitor.hallNo,
    "N/A"
  ),
  standNumber: asValueField(
    exhibitor.standNumber ?? exhibitor.stallNo ?? exhibitor.boothNo,
    "N/A"
  ),
  exhibitArrival: asValueField(exhibitor.exhibitArrival, ""),
  materialArrival: asValueField(exhibitor.materialArrival, ""),
  dismantling: asValueField(exhibitor.dismantling, ""),
  contactSalutation: asValueField(exhibitor.contactSalutation, ""),
  contactFirstName: asValueField(
    exhibitor.contactFirstName ?? exhibitor.firstName,
    ""
  ),
  contactLastName: asValueField(
    exhibitor.contactLastName ?? exhibitor.lastName,
    ""
  ),
  mobile: asValueField(exhibitor.mobile ?? exhibitor.mobileNo ?? exhibitor.phone, "N/A"),
  contactEmail: asValueField(
    exhibitor.contactEmail ?? exhibitor.email ?? exhibitor.companyEmail,
    "N/A"
  ),
});

// --- FRONTEND EMAIL PREVIEW GENERATOR ---
const generateEmailPreview = (submission, headerUrl, footerUrl) => {
  const supplier = submission.selectedSupplier || {};

  // Real exhibitor data mapped from the current API response.
  // Email rendering below is otherwise kept the same as the reference component.
  const mockUser = normalizeExhibitorForEmail(
    submission.exhibitorDetails || {},
    submission
  );

  const mockMembers = [];

  let myTemplate = `<table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; margin-top: 20px;">
    <thead><tr></tr></thead><tbody>`;

  if (submission.form && Array.isArray(submission.form)) {
    submission.form.forEach((form) => {
      if (form.type === "DynamicTable") {
        const { properties } = form;
        let tableHtml = `<tr><td colspan="2"><table style="width: 100%; border-collapse: collapse;"><thead><tr>`;
        properties.headers?.forEach((header) => {
          tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: left;">${header.header}</th>`;
        });
        tableHtml += `</tr></thead><tbody>`;
        properties.value?.forEach((row) => {
          tableHtml += "<tr>";
          properties.headers?.forEach((header) => {
            const cell = row[header.key] !== undefined ? row[header.key] : "";
            tableHtml += `<td style="border: 1px solid #ddd; padding: 8px;">${cell}</td>`;
          });
          tableHtml += "</tr>";
        });
        tableHtml += `</tbody></table></td></tr>`;
        myTemplate += tableHtml;
      } else if (
        [
          "LabelInput",
          "Select",
          "TextArea",
          "TextField",
          "RadioGroup",
        ].includes(form.type)
      ) {
        if (form.properties?.value) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${form.properties?.value}</td>
          </tr>`;
        }
      } else if (form.type === "Checkbox") {
        if (form.properties?.checked) {
          myTemplate += `<tr>
            <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><input type="checkbox" checked disabled></td>
          </tr>`;
        }
      } else if (form.type === "DatePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleDateString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "TimePicker") {
        const val = form.properties?.value
          ? new Date(form.properties.value).toLocaleTimeString(
              "en-IN",
              TimeZoneIndia
            )
          : "";
        myTemplate += `<tr>
          <td style="padding: 8px; font-weight: bold; border: 1px solid #ddd; width: 40%;">${form.properties?.label}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${val}</td>
        </tr>`;
      } else if (form.type === "RatedItems") {
        const { properties } = form;
        myTemplate += `<tr><td colspan="2">${generateRatedItemsHTML(
          properties.headers || [],
          properties.items || [],
          properties.columnSelection,
          properties.selectedColumn,
          properties.showTotal
        )}</td></tr>`;
      } else if (form.type === "TeamMembers") {
        myTemplate += `
          <tr><td colspan="2">${generateBadgeEntitlementHTML(
            form.properties,
            mockMembers,
            mockUser
          )}</td></tr>
          <tr><td colspan="2">${generateMembersTableHTML(mockMembers)}</td></tr>
        `;
      } else if (form.type === "PurchaseItems") {
        myTemplate += `<tr><td colspan="2">${generateCartTemplate(
          form.properties?.value || []
        )}</td></tr>`;
      }
    });
  }

  myTemplate += `</tbody></table>`;

  const finalHeaderUrl =
    headerUrl || "https://via.placeholder.com/800x150?text=Event+Header";
  const finalFooterUrl =
    footerUrl || "https://via.placeholder.com/800x100?text=Event+Footer";

  return oesTemplate(
    mockUser,
    myTemplate,
    supplier,
    submission.name,
    finalHeaderUrl,
    finalFooterUrl
  );
};


// -----------------------------------------------------------------------------
// TABLE MODEL HELPERS
// These helpers do NOT change the email preview. They only create the selected
// form table using the same form definitions and calculations used above.
// -----------------------------------------------------------------------------

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const formatSubmittedDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", TimeZoneIndia);
};

const formatDatePickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", TimeZoneIndia);
};

const formatTimePickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString("en-IN", TimeZoneIndia);
};

const moneyLikeHeader = (text = "") =>
  /(price|rate|amount|total|cost|charge|subtotal|₹|inr)/i.test(String(text));

const makeColumn = (key, label, options = {}) => ({
  key,
  label,
  type: options.type || "text",
  align: options.align || "left",
  minWidth: options.minWidth || 140,
  order: options.order ?? 100,
});

const valueToDisplayString = (value) => {
  if (!hasValue(value)) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object" && item !== null
          ? JSON.stringify(item)
          : String(item)
      )
      .join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatTableValue = (value, column) => {
  if (!hasValue(value)) return "—";

  if (column?.type === "money") {
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return `₹ ${number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  if (column?.type === "number") {
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString("en-IN");
  }

  return valueToDisplayString(value);
};

const extractSubmissionArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.oesSubmissions)) return payload.oesSubmissions;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.submissions)) return payload.submissions;
  if (Array.isArray(payload.result)) return payload.result;
  if (Array.isArray(payload.items)) return payload.items;
  if (payload.data && Array.isArray(payload.data.oesSubmissions)) {
    return payload.data.oesSubmissions;
  }
  return [];
};

const extractExhibitorArray = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.exhibitors)) return payload.exhibitors;
  if (payload.data && Array.isArray(payload.data.exhibitors)) {
    return payload.data.exhibitors;
  }
  return [];
};

const getSubmittedById = (submission = {}) => {
  const submittedBy = submission.submittedBy;
  if (submittedBy && typeof submittedBy === "object") {
    return submittedBy._id || submittedBy.id || "";
  }
  return submittedBy || "";
};

const getCompanyName = (exhibitor = {}, fallback = "") =>
  exhibitor.companyName?.value ||
  exhibitor.companyName ||
  fallback ||
  "Unknown Exhibitor";

const getFormName = (submission = {}) =>
  submission.name ||
  submission.formName ||
  submission.title ||
  (typeof submission.form === "string" ? submission.form : "") ||
  "OES Form";

const getVendorName = (submission = {}) =>
  submission.selectedSupplier?.supplierName ||
  submission.supplier?.supplierName ||
  submission.supplierName ||
  "N/A";

const getFormElements = (submission = {}) =>
  Array.isArray(submission.form) ? submission.form : [];

const getRatedHeaderCellValue = (header, row, i) => {
  if (header.type === "label") {
    return row[`key${i + 1}`] || "";
  }

  if (header.type === "label checkbox") {
    const checked = Boolean(row[`value${i}`]);
    const label = row[`key${i + 1}`] || "";
    return `${checked ? "☑" : "☐"} ${label}`.trim();
  }

  if (header.type === "input text" || header.type === "input number") {
    return row[`value${i}`] ?? "";
  }

  if (header.type === "input date range") {
    const dateFrom = row[`dateFrom${i}`]
      ? new Date(row[`dateFrom${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";
    const dateTo = row[`dateTo${i}`]
      ? new Date(row[`dateTo${i}`]).toLocaleDateString("en-IN", TimeZoneIndia)
      : "";

    // Keep the same display order as the reference email preview's renderCell().
    return `From: ${dateTo}\nTo: ${dateFrom}`;
  }

  if (header.type === "input checkbox") {
    return row[`value${i}`] ? "Checked" : "Unchecked";
  }

  if (header.type === "calc") {
    return row[`calcVal${i}`] ?? "";
  }

  return "";
};

const prepareRatedItemsForTable = (
  headers = [],
  itemsList = [],
  isColumnSelection = false,
  selectedColumn = null,
  totalRequired = false
) => {
  const columnsWithCheckedItems = new Set();

  if (isColumnSelection && selectedColumn != null) {
    itemsList.forEach((item) => {
      headers.forEach((header, i) => {
        if (
          i !== selectedColumn &&
          (header.type === "input checkbox" ||
            header.type === "label checkbox") &&
          item[`value${i}`]
        ) {
          columnsWithCheckedItems.add(i);
        }
      });
    });
  }

  // Same calculation path as generateRatedItemsHTML().
  const itemsWithSubtotal = itemsList.map((item, rowIndex) => {
    const newItem = { ...item };

    if (isColumnSelection && selectedColumn != null) {
      const isChecked = item[`value${selectedColumn}`];
      const valueKey = `key${selectedColumn + 1}`;
      const rawValue = item[valueKey];
      const value = parseFloat(String(rawValue).replace(/,/g, "")) || 0;
      newItem.subtotal = isChecked ? value : 0;
    } else {
      const calcIndex = headers.findIndex((item) => item.type === "calc");
      if (calcIndex !== -1) {
        newItem[`calcVal${calcIndex}`] = subTotal(
          rowIndex,
          calcIndex,
          itemsList,
          headers
        );
        newItem.subtotal = newItem[`calcVal${calcIndex}`] || 0;
      } else {
        newItem.subtotal = 0;
      }
    }

    return newItem;
  });

  const filteredItems = itemsWithSubtotal.filter((item) => {
    if (isColumnSelection && selectedColumn != null) {
      return item[`value${selectedColumn}`];
    }
    return item.subtotal > 0;
  });

  const totalValue = totalRequired
    ? filteredItems.reduce((acc, item) => acc + (item.subtotal || 0), 0)
    : 0;

  const visibleIndexes = headers
    .map((header, i) => ({ header, i }))
    .filter(({ header, i }) =>
      !isColumnSelection ||
      i === selectedColumn ||
      columnsWithCheckedItems.has(i) ||
      (header.type !== "input checkbox" && header.type !== "label checkbox")
    );

  const columns = visibleIndexes.map(({ header, i }, position) => {
    const isNumeric =
      header.type === "input number" || header.type === "calc";
    const isMoney = moneyLikeHeader(header.header);

    return makeColumn(`rated-${i}`, header.header || `Column ${i + 1}`, {
      type: isMoney ? "money" : isNumeric ? "number" : "text",
      align: isNumeric || isMoney ? "right" : "left",
      minWidth: header.type === "input date range" ? 180 : 140,
      order: position,
    });
  });

  if (isColumnSelection && selectedColumn != null) {
    columns.push(
      makeColumn("rated-subtotal", "Subtotal", {
        type: "money",
        align: "right",
        minWidth: 130,
        order: columns.length,
      })
    );
  }

  const rows = filteredItems.map((row, rowIndex) => {
    const values = {};

    visibleIndexes.forEach(({ header, i }) => {
      values[`rated-${i}`] = getRatedHeaderCellValue(header, row, i);
    });

    if (isColumnSelection && selectedColumn != null) {
      values["rated-subtotal"] = row.subtotal || 0;
    }

    return { rowIndex, values };
  });

  let summary = null;
  if (totalRequired) {
    const gst = Math.round(totalValue * 0.18);
    const grandTotal = Math.round(totalValue + gst);
    summary = {
      financial: true,
      totalValue,
      grandTotal,
      rows: [
        { label: "Total", value: totalValue, type: "money" },
        { label: "18% GST", value: gst, type: "money" },
        { label: "Grand Total", value: grandTotal, type: "money", strong: true },
      ],
    };
  }

  return { columns, rows, summary };
};

const preparePurchaseItemsForTable = (cartItems = []) => {
  let subtotal = 0;

  const rows = cartItems.map((item, rowIndex) => {
    const itemTotal = (item.price || 0) * (item.quantity || 0);
    subtotal += itemTotal;

    return {
      rowIndex,
      values: {
        purchaseName: item.name || "",
        purchaseQty: item.quantity || 0,
        purchasePrice: item.price || 0,
        purchaseTotal: itemTotal,
      },
    };
  });

  const gst = subtotal * 0.18;
  const grandTotal = subtotal + gst;

  return {
    columns: [
      makeColumn("purchaseName", "Item Name", { minWidth: 240, order: 1 }),
      makeColumn("purchaseQty", "Quantity", {
        type: "number",
        align: "center",
        minWidth: 90,
        order: 2,
      }),
      makeColumn("purchasePrice", "Price (₹)", {
        type: "money",
        align: "right",
        minWidth: 120,
        order: 3,
      }),
      makeColumn("purchaseTotal", "Total (₹)", {
        type: "money",
        align: "right",
        minWidth: 130,
        order: 4,
      }),
    ],
    rows,
    summary: {
      financial: true,
      totalValue: subtotal,
      grandTotal,
      rows: [
        { label: "Subtotal", value: subtotal, type: "money" },
        { label: "GST (18%)", value: gst, type: "money" },
        { label: "Grand Total", value: grandTotal, type: "money", strong: true },
      ],
    },
  };
};

const prepareDynamicTableForTable = (properties = {}) => {
  const headers = Array.isArray(properties.headers) ? properties.headers : [];
  const valueRows = Array.isArray(properties.value) ? properties.value : [];

  const columns = headers.map((header, index) =>
    makeColumn(`dynamic-${index}`, header.header || header.label || header.key || `Column ${index + 1}`, {
      minWidth: 150,
      order: index,
    })
  );

  const rows = valueRows.map((row, rowIndex) => {
    const values = {};
    headers.forEach((header, index) => {
      values[`dynamic-${index}`] = row?.[header.key] ?? "";
    });
    return { rowIndex, values };
  });

  return { columns, rows, summary: null };
};

const getMembersForTable = (form, submission) => {
  const candidates = [
    form?.properties?.value,
    form?.properties?.members,
    form?.properties?.items,
    submission?.members,
    submission?.teamMembers,
    submission?.teamMemberList,
  ];
  return candidates.find(Array.isArray) || [];
};

const prepareTeamMembersForTable = (form, submission, exhibitorUser) => {
  const members = getMembersForTable(form, submission);
  const properties = form?.properties || {};

  const {
    basenum = 0,
    basesqm = 0,
    extranum = 0,
    extrasqm = 1,
  } = properties;

  const intBasenum = parseInt(basenum, 10) || 0;
  const intBasesqm = parseInt(basesqm, 10) || 0;
  const intExtranum = parseInt(extranum, 10) || 0;
  const intExtrasqm = parseInt(extrasqm, 10) || 1;
  const mySize = parseFloat(exhibitorUser?.standSize?.value || 0);
  const totalFreeBadges =
    intBasenum +
    Math.ceil(Math.max(0, mySize - intBasesqm) * (intExtranum / intExtrasqm));
  const totalMembers = members.length;
  const paidBadges = Math.max(0, totalMembers - totalFreeBadges);

  const rows = members.map((member, rowIndex) => ({
    rowIndex,
    values: {
      memberProfile: member.profile_image || DEFAULT_DP,
      memberFirstName: member.firstName || "",
      memberLastName: member.lastName || "",
      memberPhone: member.phone || "",
      memberEmail: member.email || "",
      memberRole: member.role || "",
      memberDesignation: member.designation || "",
    },
  }));

  return {
    columns: [
      makeColumn("memberProfile", "Profile Image", { minWidth: 150, order: 1 }),
      makeColumn("memberFirstName", "First Name", { minWidth: 140, order: 2 }),
      makeColumn("memberLastName", "Last Name", { minWidth: 140, order: 3 }),
      makeColumn("memberPhone", "Phone", { minWidth: 140, order: 4 }),
      makeColumn("memberEmail", "Email", { minWidth: 210, order: 5 }),
      makeColumn("memberRole", "Role", { minWidth: 140, order: 6 }),
      makeColumn("memberDesignation", "Designation", { minWidth: 180, order: 7 }),
    ],
    rows,
    summary: {
      financial: false,
      rows: [
        { label: "Total Free Badges", value: totalFreeBadges, type: "number" },
        { label: "Paid Badges Required", value: paidBadges, type: "number" },
        { label: "Total Members", value: totalMembers, type: "number", strong: true },
      ],
    },
  };
};

const unwrapValue = (value) => {
  if (
    value &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "value")
  ) {
    return value.value;
  }
  return value;
};

const prepareCommonExhibitorFields = (exhibitorUser = {}, formName = "") => {
  const columns = [
    makeColumn("common-company", "Company Name", {
      minWidth: 190,
      order: 0,
    }),
    makeColumn("common-space", "Raw Space", {
      minWidth: 130,
      order: 1,
    }),
    makeColumn("common-stand-size", "Stand Size", {
      minWidth: 110,
      order: 2,
    }),
    makeColumn("common-hall", "Hall Number", {
      minWidth: 110,
      order: 3,
    }),
    makeColumn("common-stand", "Stand Number", {
      minWidth: 120,
      order: 4,
    }),
  ];

  const values = {
    "common-company": unwrapValue(exhibitorUser.companyName) || "N/A",
    "common-space": unwrapValue(exhibitorUser.spaceType) || "N/A",
    "common-stand-size": unwrapValue(exhibitorUser.standSize) || "N/A",
    "common-hall": unwrapValue(exhibitorUser.hallNumber) || "N/A",
    "common-stand": unwrapValue(exhibitorUser.standNumber) || "N/A",
  };

  // Match getExhibitorInfoOes(): these three dates only belong to the
  // setup + dismantling form context.
  const title = String(formName || "");
  if (/setup/i.test(title) && /dismantling/i.test(title)) {
    columns.push(
      makeColumn("common-exhibit-arrival", "Exhibits Arrival Date", {
        minWidth: 155,
        order: 5,
      }),
      makeColumn("common-material-arrival", "Material Arrival Date", {
        minWidth: 155,
        order: 6,
      }),
      makeColumn("common-dismantling", "Dismantling Date", {
        minWidth: 145,
        order: 7,
      })
    );

    values["common-exhibit-arrival"] = formatDatePickerValue(
      unwrapValue(exhibitorUser.exhibitArrival)
    );
    values["common-material-arrival"] = formatDatePickerValue(
      unwrapValue(exhibitorUser.materialArrival)
    );
    values["common-dismantling"] = formatDatePickerValue(
      unwrapValue(exhibitorUser.dismantling)
    );
  }

  return { columns, values };
};

const prepareScalarFieldsForTable = (
  elements = [],
  {
    includeCommonExhibitor = false,
    exhibitorUser = {},
    formName = "",
  } = {}
) => {
  const columns = [];
  const values = {};

  if (includeCommonExhibitor) {
    const common = prepareCommonExhibitorFields(exhibitorUser, formName);
    columns.push(...common.columns);
    Object.assign(values, common.values);
  }

  let nextOrder = columns.length;

  elements.forEach((form, index) => {
    const properties = form?.properties || {};
    const label = properties.label || properties.header || properties.title;

    // Internal configuration is never shown as a cell. Only the actual body
    // value represented by the component is converted into a dashboard column.
    if (!label) return;

    const key = `field-${index}`;
    let value;
    let shouldInclude = false;

    if (
      ["LabelInput", "Select", "TextArea", "TextField", "RadioGroup"].includes(
        form.type
      )
    ) {
      // Same body rule as generateEmailPreview(): render when a value exists.
      if (properties.value) {
        value = properties.value;
        shouldInclude = true;
      }
    } else if (form.type === "Checkbox") {
      // Same body rule: only checked checkboxes are rendered.
      if (properties.checked) {
        value = "Checked";
        shouldInclude = true;
      }
    } else if (form.type === "DatePicker") {
      // DatePicker / TimePicker are body fields; retain the column even if the
      // current submission has no value so the selected form keeps its schema.
      value = formatDatePickerValue(properties.value);
      shouldInclude = true;
    } else if (form.type === "TimePicker") {
      value = formatTimePickerValue(properties.value);
      shouldInclude = true;
    }

    if (!shouldInclude) return;

    columns.push(
      makeColumn(key, label, {
        minWidth: form.type === "TextArea" ? 260 : 170,
        order: nextOrder++,
      })
    );
    values[key] = value;
  });

  return {
    columns,
    rows: columns.length ? [{ rowIndex: 0, values }] : [],
    summary: null,
  };
};

const attachScalarBodyColumns = (prepared, scalarPrepared) => {
  const scalarColumns = scalarPrepared?.columns || [];
  const scalarValues = scalarPrepared?.rows?.[0]?.values || {};

  if (scalarColumns.length === 0) return prepared;

  // Scalar body fields belong to the same submission. Put them before the
  // line-item/table columns and repeat them per body-table row so Excel/search
  // and the on-screen table remain a flat representation of the email body.
  const shiftedTableColumns = (prepared.columns || []).map((column, index) => ({
    ...column,
    order: scalarColumns.length + index,
  }));

  let rows = prepared.rows || [];
  if (rows.length === 0) {
    rows = [{ rowIndex: 0, values: {} }];
  }

  rows = rows.map((row) => ({
    ...row,
    values: {
      ...scalarValues,
      ...(row.values || {}),
    },
  }));

  return {
    ...prepared,
    columns: [...scalarColumns, ...shiftedTableColumns],
    rows,
  };
};

const buildSubmissionTableModel = (submission, index = 0) => {
  const elements = getFormElements(submission);
  const submissionId = String(
    submission._id || submission.id || `submission-${index}`
  );
  const exhibitorUser = normalizeExhibitorForEmail(
    submission.exhibitorDetails || {},
    submission
  );

  const formName = getFormName(submission);

  const base = {
    submissionId,
    form: formName,
    vendor: getVendorName(submission),
    exhibitor:
      submission.exhibitorName ||
      getCompanyName(exhibitorUser, getSubmittedById(submission)),
    submittedOn:
      submission.submittedOn ||
      submission.createdOn ||
      submission.createdAt ||
      "",
    rawSubmission: submission,
  };

  const bodyTableTypes = new Set([
    "RatedItems",
    "PurchaseItems",
    "DynamicTable",
    "TeamMembers",
  ]);

  // Simple body values are real form values and can accompany a body table.
  // They are NOT the component's `properties` object.
  const scalarElements = elements.filter((item) => !bodyTableTypes.has(item?.type));
  const scalarBody = prepareScalarFieldsForTable(scalarElements);

  // Use the table-producing body component as the primary row schema.
  // This is exactly the same body family handled by generateEmailPreview().
  const primary = elements.find((item) => bodyTableTypes.has(item?.type));

  let prepared;
  let kind = "fields";

  if (primary?.type === "RatedItems") {
    const properties = primary.properties || {};
    prepared = prepareRatedItemsForTable(
      properties.headers || [],
      properties.items || [],
      properties.columnSelection,
      properties.selectedColumn,
      properties.showTotal
    );
    kind = "rated";
  } else if (primary?.type === "PurchaseItems") {
    prepared = preparePurchaseItemsForTable(primary.properties?.value || []);
    kind = "purchase";
  } else if (primary?.type === "DynamicTable") {
    prepared = prepareDynamicTableForTable(primary.properties || {});
    kind = "dynamic";
  } else if (primary?.type === "TeamMembers") {
    prepared = prepareTeamMembersForTable(primary, submission, exhibitorUser);
    kind = "team";
  } else {
    // No body table exists. Only in this case include the common exhibitor
    // fields (Company, Space, Stand Size, Hall, Stand No) together with the
    // actual scalar fields from the email body.
    prepared = prepareScalarFieldsForTable(elements, {
      includeCommonExhibitor: true,
      exhibitorUser,
      formName,
    });
    kind = prepared.columns.length ? "fields" : "empty";
  }

  // If a real body table exists, DO NOT add common exhibitor fields. However,
  // scalar body fields (remarks/date/select/etc.) are still legitimate email
  // body data, so attach them as normal columns rather than hiding them inside
  // a `properties` cell.
  if (primary && scalarBody.columns.length > 0) {
    prepared = attachScalarBodyColumns(prepared, scalarBody);
  }

  const rows = (prepared.rows || []).map((row, rowIndex) => ({
    ...base,
    ...row,
    _id: `${submissionId}-row-${rowIndex + 1}`,
  }));

  return {
    ...base,
    kind,
    columns: prepared.columns || [],
    rows,
    summary: prepared.summary,
    isFinancial: Boolean(prepared.summary?.financial),
  };
};

const mergeColumns = (models = []) => {
  const map = new Map();
  models.forEach((model) => {
    model.columns.forEach((column) => {
      if (!map.has(column.key)) map.set(column.key, column);
    });
  });
  return Array.from(map.values()).sort((a, b) => a.order - b.order);
};

const kindLabel = (kind) => {
  if (kind === "rated") return "Rated Items";
  if (kind === "purchase") return "Purchase Items";
  if (kind === "dynamic") return "Dynamic Table";
  if (kind === "team") return "Team Members";
  if (kind === "fields") return "Form Responses";
  return "No Table Data";
};

const getFormBadgeStyle = (formName) => {
  const str = String(formName || "").toLowerCase();
  if (str.includes("audio") || str.includes("visual")) {
    return { bgcolor: "#dcfce7", color: "#15803d" };
  }
  if (str.includes("electrical") || str.includes("power")) {
    return { bgcolor: "#dbeafe", color: "#1d4ed8" };
  }
  if (str.includes("furniture")) {
    return { bgcolor: "#fef3c7", color: "#b45309" };
  }
  if (str.includes("freight") || str.includes("logistics")) {
    return { bgcolor: "#ffe4e6", color: "#be123c" };
  }
  return { bgcolor: "#e2e8f0", color: "#334155" };
};


const OesExplorer = () => {
  // Admin data is preferred when it contains submissions. User data is also
  // loaded because /user/get-oes-submissions carries the exhibitor collection
  // used to replace the mocked email-preview user.
  const {
    data: adminOesData,
    isLoading: isAdminLoading,
    isError: isAdminError,
    error: adminError,
  } = useApiEp(`/admin/get-all-oes-submit`);

  const {
    data: userOesData,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
  } = useApiEp(`/user/get-oes-submissions`);

  const {
    data: headerFooterData,
    isLoading: isHeaderFooterLoading,
  } = useApiEp(`/event/visitor-reg-email-header-footer`);

  const headerUrl = headerFooterData?.headerUrl || "";
  const footerUrl = headerFooterData?.footerUrl || "";

  const [selectedFormFilter, setSelectedFormFilter] = useState("");
  const [selectedVendorFilter, setSelectedVendorFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const exhibitors = useMemo(() => {
    const map = new Map();
    [...extractExhibitorArray(adminOesData), ...extractExhibitorArray(userOesData)].forEach((item) => {
      const id = String(item?._id || item?.id || "");
      if (id && !map.has(id)) map.set(id, item);
    });
    return Array.from(map.values());
  }, [adminOesData, userOesData]);

  const allSubmissions = useMemo(() => {
    const adminList = extractSubmissionArray(adminOesData);
    const userList = extractSubmissionArray(userOesData);
    const source = adminList.length > 0 ? adminList : userList;

    return source.map((submission) => {
      const submittedById = String(getSubmittedById(submission));
      const existingExhibitor =
        submission.exhibitorDetails ||
        (submission.user && typeof submission.user === "object" ? submission.user : null) ||
        exhibitors.find((item) => String(item?._id || item?.id || "") === submittedById) ||
        {};

      return {
        ...submission,
        name: submission.name || getFormName(submission),
        selectedSupplier:
          submission.selectedSupplier ||
          submission.supplier ||
          submission.supplierDetails ||
          {},
        exhibitorDetails: existingExhibitor,
        exhibitorName: getCompanyName(
          existingExhibitor,
          submission.exhibitorName || submittedById
        ),
      };
    });
  }, [adminOesData, userOesData, exhibitors]);

  const allModels = useMemo(
    () => allSubmissions.map((submission, index) => buildSubmissionTableModel(submission, index)),
    [allSubmissions]
  );

  const formOptions = useMemo(
    () =>
      Array.from(
        new Set(
          allModels
            .map((model) => model.form)
            .filter((name) => name && name !== "N/A")
        )
      ).sort(),
    [allModels]
  );

  const selectedModels = useMemo(() => {
    if (!selectedFormFilter) return [];
    return allModels.filter((model) => model.form === selectedFormFilter);
  }, [allModels, selectedFormFilter]);

  const selectedKind = useMemo(() => {
    const counts = new Map();
    selectedModels.forEach((model) => {
      if (model.kind !== "empty") {
        counts.set(model.kind, (counts.get(model.kind) || 0) + 1);
      }
    });

    if (counts.size === 0) return "empty";

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])[0][0];
  }, [selectedModels]);

  const selectedKindModels = useMemo(
    () => selectedModels.filter((model) => model.kind === selectedKind),
    [selectedModels, selectedKind]
  );

  const selectedColumns = useMemo(
    () => mergeColumns(selectedKindModels),
    [selectedKindModels]
  );

  const vendorOptions = useMemo(
    () =>
      Array.from(
        new Set(
          selectedModels
            .map((model) => model.vendor)
            .filter((name) => name && name !== "N/A")
        )
      ).sort(),
    [selectedModels]
  );

  const filteredModels = useMemo(() => {
    if (!selectedFormFilter || selectedKind === "empty") return [];
    const query = searchQuery.trim().toLowerCase();

    return selectedKindModels.filter((model) => {
      const vendorMatch =
        selectedVendorFilter === "ALL" || model.vendor === selectedVendorFilter;

      const rowText = model.rows
        .flatMap((row) => Object.values(row.values || {}))
        .map(valueToDisplayString)
        .join(" ")
        .toLowerCase();

      const summaryText = (model.summary?.rows || [])
        .map((item) => `${item.label} ${item.value}`)
        .join(" ")
        .toLowerCase();

      const queryMatch =
        !query ||
        model.vendor.toLowerCase().includes(query) ||
        model.exhibitor.toLowerCase().includes(query) ||
        formatSubmittedDateTime(model.submittedOn).toLowerCase().includes(query) ||
        rowText.includes(query) ||
        summaryText.includes(query);

      return vendorMatch && queryMatch;
    });
  }, [
    selectedFormFilter,
    selectedKind,
    selectedKindModels,
    selectedVendorFilter,
    searchQuery,
  ]);

  const paginatedModels = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredModels.slice(start, start + rowsPerPage);
  }, [filteredModels, page]);

  const totalPages = Math.ceil(filteredModels.length / rowsPerPage) || 1;

  const totalRowsOnPage = useMemo(
    () => paginatedModels.reduce((sum, model) => sum + model.rows.length, 0),
    [paginatedModels]
  );

  const vendorCount = useMemo(
    () =>
      new Set(
        allModels
          .map((model) => model.vendor)
          .filter((name) => name && name !== "N/A")
      ).size,
    [allModels]
  );

  const exhibitorCount = useMemo(
    () =>
      new Set(
        allModels
          .map((model) => model.exhibitor)
          .filter((name) => name && name !== "N/A")
      ).size,
    [allModels]
  );

  const selectedHasFinancialValues = selectedKindModels.some(
    (model) => model.isFinancial
  );

  const totalAmount = useMemo(() => {
    if (!selectedHasFinancialValues) return 0;
    return filteredModels.reduce(
      (sum, model) => sum + Number(model.summary?.grandTotal || 0),
      0
    );
  }, [filteredModels, selectedHasFinancialValues]);

  const handleReset = () => {
    setSelectedFormFilter("");
    setSelectedVendorFilter("ALL");
    setSearchQuery("");
    setPage(1);
  };

  const handleOpenDialog = (modelOrRow) => {
    const submission = modelOrRow?.rawSubmission || modelOrRow;
    if (!submission) return;
    setSelectedSubmission(submission);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSubmission(null);
  };

  const handleExport = () => {
    if (!selectedFormFilter || filteredModels.length === 0) return;

    const output = [];

    filteredModels.forEach((model) => {
      model.rows.forEach((row, rowIndex) => {
        const record = {
          Form: model.form,
          Vendor: model.vendor,
          Exhibitor: model.exhibitor,
          "Submitted On": formatSubmittedDateTime(model.submittedOn),
        };

        selectedColumns.forEach((column) => {
          const value = row.values?.[column.key];
          record[column.label] = hasValue(value) ? valueToDisplayString(value) : "";
        });

        if (rowIndex === 0 && model.summary?.rows?.length) {
          model.summary.rows.forEach((summaryRow) => {
            record[summaryRow.label] = summaryRow.value;
          });
        }

        output.push(record);
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(output);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendor Transactions");
    XLSX.writeFile(
      workbook,
      `${selectedFormFilter.replace(/[^a-z0-9]+/gi, "_")}_Vendor_Transaction_Details.xlsx`
    );
  };

  const isLoading = isAdminLoading && isUserLoading;
  const effectiveError =
    extractSubmissionArray(adminOesData).length === 0 &&
    extractSubmissionArray(userOesData).length === 0
      ? adminError || userError
      : null;

  if (isLoading) return <MuPb />;

  if (effectiveError && isAdminError && isUserError) {
    return <Alert severity="error">{getErrorResponse(effectiveError)}</Alert>;
  }

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 3, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center">
            <BackBtn />
            <Typography variant="h5" ml={2} fontWeight="700" color="#0f172a">
              OES Submissions & Vendor Transactions
            </Typography>
            <Chip
              label="Live Sync"
              size="small"
              sx={{
                ml: 2,
                bgcolor: "#dcfce7",
                color: "#15803d",
                fontWeight: 700,
                borderRadius: "6px",
              }}
            />
          </Box>

          <Chip
            icon={<CalendarTodayIcon sx={{ fontSize: "14px !important" }} />}
            label={`${allSubmissions.length} submissions`}
            variant="outlined"
            sx={{ bgcolor: "#fff", fontWeight: 600 }}
          />
        </Box>

        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ p: 2.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <DescriptionIcon sx={{ color: "#2563eb", fontSize: 32 }} />
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={700}>
                    FORMS
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>{formOptions.length}</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ p: 2.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <StorefrontIcon sx={{ color: "#059669", fontSize: 32 }} />
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={700}>
                    VENDORS
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>{vendorCount}</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ p: 2.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <GroupIcon sx={{ color: "#7c3aed", fontSize: 32 }} />
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={700}>
                    EXHIBITORS
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>{exhibitorCount}</Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ p: 2.5, border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <Box>
                <Typography variant="caption" color="#64748b" fontWeight={700}>
                  SELECTED FORM TOTAL
                </Typography>
                <Typography variant="h5" fontWeight={800} mt={0.8}>
                  {selectedFormFilter && selectedHasFinancialValues
                    ? `₹ ${totalAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : "—"}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            bgcolor: "#fff",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon sx={{ color: "#2563eb" }} />
              <Typography variant="h6" fontWeight={700} fontSize="1rem">
                Select Form & Filter
              </Typography>
            </Box>

            {(selectedFormFilter || selectedVendorFilter !== "ALL" || searchQuery) && (
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                sx={{ textTransform: "none", color: "#ef4444", fontWeight: 600 }}
              >
                Reset
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="#475569" fontWeight={700} display="block" mb={0.6}>
                Select Form
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedFormFilter}
                  displayEmpty
                  onChange={(event) => {
                    setSelectedFormFilter(event.target.value);
                    setSelectedVendorFilter("ALL");
                    setSearchQuery("");
                    setPage(1);
                  }}
                >
                  <MenuItem value="" disabled>
                    Choose a form
                  </MenuItem>
                  {formOptions.map((form) => (
                    <MenuItem key={form} value={form}>{form}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="#475569" fontWeight={700} display="block" mb={0.6}>
                Vendor
              </Typography>
              <FormControl fullWidth size="small" disabled={!selectedFormFilter}>
                <Select
                  value={selectedVendorFilter}
                  onChange={(event) => {
                    setSelectedVendorFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="ALL">All Vendors</MenuItem>
                  {vendorOptions.map((vendor) => (
                    <MenuItem key={vendor} value={vendor}>{vendor}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="#475569" fontWeight={700} display="block" mb={0.6}>
                Search
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                disabled={!selectedFormFilter}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="Search selected form..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={!selectedFormFilter || filteredModels.length === 0}
                sx={{ mt: { xs: 0, md: 2.7 }, textTransform: "none", fontWeight: 700 }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Card>

        {!selectedFormFilter ? (
          <Card
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              border: "1px dashed #cbd5e1",
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            <TableChartIcon sx={{ fontSize: 38, color: "#94a3b8", mb: 1 }} />
            <Typography fontWeight={700} color="#334155">
              Select a form to show Vendor Transaction Details
            </Typography>
            <Typography variant="body2" color="#64748b" mt={0.75}>
              Columns and calculated rows are generated from that form&apos;s actual component type.
            </Typography>
          </Card>
        ) : selectedColumns.length === 0 ? (
          <Alert severity="info">
            The selected form has no displayable submitted values.
          </Alert>
        ) : (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              bgcolor: "#fff",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <Box
              px={3}
              py={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              borderBottom="1px solid #e2e8f0"
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <TableChartIcon />
                <Typography variant="h6" fontSize="1rem" fontWeight={700}>
                  Vendor Transaction Details
                </Typography>
                <Chip
                  size="small"
                  label={selectedFormFilter}
                  sx={{
                    ...getFormBadgeStyle(selectedFormFilter),
                    fontWeight: 700,
                  }}
                />
                <Chip size="small" variant="outlined" label={kindLabel(selectedKind)} />
              </Box>

              <Chip
                size="small"
                label={`${paginatedModels.length} of ${filteredModels.length} submissions · ${totalRowsOnPage} rows`}
                sx={{ bgcolor: "#f1f5f9", fontWeight: 600 }}
              />
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ overflowX: "auto" }}>
              <MuiTable
                sx={{
                  minWidth: Math.max(
                    900,
                    570 + selectedColumns.reduce((sum, col) => sum + (col.minWidth || 140), 0)
                  ),
                }}
              >
                <MuiTableHead sx={{ bgcolor: "#f8fafc" }}>
                  <MuiTableRow>
                    <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>VENDOR</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>EXHIBITOR</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>SUBMITTED ON</TableCell>
                    {selectedColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align}
                        sx={{ fontWeight: 800, minWidth: column.minWidth, whiteSpace: "nowrap" }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 800, minWidth: 120 }}>
                      EMAIL PREVIEW
                    </TableCell>
                  </MuiTableRow>
                </MuiTableHead>

                <MuiTableBody>
                  {paginatedModels.length > 0 ? (
                    paginatedModels.map((model) => (
                      <React.Fragment key={model.submissionId}>
                        {model.rows.map((row, rowIndex) => (
                          <MuiTableRow key={row._id} hover>
                            <TableCell>
                              {rowIndex === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="#2563eb"
                                  fontWeight={700}
                                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                  onClick={() => handleOpenDialog(model)}
                                >
                                  {model.vendor}
                                </Typography>
                              ) : "—"}
                            </TableCell>

                            <TableCell>
                              {rowIndex === 0 ? (
                                <Chip size="small" variant="outlined" label={model.exhibitor} />
                              ) : "—"}
                            </TableCell>

                            <TableCell>
                              {rowIndex === 0 ? formatSubmittedDateTime(model.submittedOn) : "—"}
                            </TableCell>

                            {selectedColumns.map((column) => {
                              const value = row.values?.[column.key];
                              const isProfileImage = column.key === "memberProfile" && value;

                              return (
                                <TableCell key={column.key} align={column.align}>
                                  {isProfileImage ? (
                                    <Box
                                      component="img"
                                      src={value}
                                      alt="Profile"
                                      sx={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: column.type === "money" ? 700 : 500,
                                        fontFamily: column.type === "money" ? "monospace" : "inherit",
                                        whiteSpace: "pre-line",
                                      }}
                                    >
                                      {formatTableValue(value, column)}
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}

                            <TableCell align="center">
                              {rowIndex === 0 ? (
                                <Tooltip title="Preview generated email">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleOpenDialog(model)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Preview
                                  </Button>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          </MuiTableRow>
                        ))}

                        {(model.summary?.rows || []).map((summaryRow, summaryIndex) => (
                          <MuiTableRow
                            key={`${model.submissionId}-summary-${summaryIndex}`}
                            sx={{
                              bgcolor: "#f8fafc",
                              borderBottom:
                                summaryIndex === model.summary.rows.length - 1
                                  ? "3px solid #e2e8f0"
                                  : undefined,
                            }}
                          >
                            <TableCell
                              colSpan={selectedColumns.length + 2}
                              align="right"
                              sx={{ fontWeight: summaryRow.strong ? 800 : 700 }}
                            >
                              {summaryRow.label}:
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: summaryRow.strong ? 800 : 700,
                                color: summaryRow.strong && model.isFinancial ? "#15803d" : "#334155",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {summaryRow.type === "money"
                                ? `₹ ${Number(summaryRow.value || 0).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                : Number(summaryRow.value || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell />
                          </MuiTableRow>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <MuiTableRow>
                      <TableCell colSpan={selectedColumns.length + 4} align="center" sx={{ py: 5 }}>
                        <Typography color="#64748b" fontWeight={600}>
                          No records match the selected form and filters.
                        </Typography>
                      </TableCell>
                    </MuiTableRow>
                  )}
                </MuiTableBody>
              </MuiTable>
            </TableContainer>

            {filteredModels.length > 0 && (
              <Box
                px={3}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderTop="1px solid #e2e8f0"
              >
                <Typography variant="caption" color="#64748b" fontWeight={600}>
                  Page {page} of {totalPages}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  size="small"
                  shape="rounded"
                  color="primary"
                />
              </Box>
            )}
          </Card>
        )}

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Email Preview - {selectedSubmission?.name || selectedSubmission?.formName}
            </Typography>
            <IconButton size="small" onClick={handleCloseDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "#f4f4f4", p: 3 }}>
            {isHeaderFooterLoading ? (
              <MuPb />
            ) : selectedSubmission ? (
              <Box
                sx={{ width: "100%", display: "flex", justifyContent: "center" }}
                dangerouslySetInnerHTML={{
                  __html: generateEmailPreview(
                    selectedSubmission,
                    headerUrl,
                    footerUrl
                  ),
                }}
              />
            ) : (
              <MuPb />
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={handleCloseDialog}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default OesExplorer;
